import { useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  FixedNav,
  Caption,
  AnimatedNumber,
  SessionTimer,
  XpFloater,
} from '@/ui';
import { openingLabApi } from './api';
import { parsePgnTree } from './parsePgnTree';
import { DrillRecall } from './DrillRecall';
import { DrillPlan } from './DrillPlan';
import { DrillPunish } from './DrillPunish';
import { DrillSurvival } from './DrillSurvival';
import { DrillMirror } from './DrillMirror';
import type { Mode } from './types';

/**
 * Opening Lab — drill (port V2).
 *
 * Refonte visuelle du wrapper : header condensé + 5 mode pills + score live,
 * mais préserve intégralement les 5 composants de drill existants (DrillRecall,
 * DrillPlan, DrillPunish, DrillSurvival, DrillMirror) — chacun a sa propre
 * logique de board + validation + POST `/training/attempts` héritée du MVP1.1.
 *
 * Le brief §3.1 dit « 5 modes existants à harmoniser visuellement ». Cette
 * harmonisation se fait au niveau du chrome (header + nav), pas du contenu
 * de chaque mode (un refactor visuel des 5 panels mode serait scope-creep
 * et briserait la stabilité MVP1.1+1.2).
 */

const MODE_META: Record<Mode, { title: string; icon: string; desc: string }> = {
  recall: {
    title: 'Mémoire',
    icon: '🧠',
    desc: 'Retrouve le coup par mémoire.',
  },
  plan: {
    title: 'Plan',
    icon: '➳',
    desc: 'Choisis le bon plan parmi trois.',
  },
  punish: {
    title: 'Riposte',
    icon: '⚡',
    desc: "Sanctionne l'imprécision typique.",
  },
  survival: {
    title: 'Tiens bon',
    icon: '🛡',
    desc: "Sortie de théorie · trouve le plan correct sans aide.",
  },
  mirror: {
    title: 'Miroir',
    icon: '🪞',
    desc: 'Position miroir horizontale · casse la mémoire visuelle.',
  },
};

const ALL_MODES: Mode[] = ['recall', 'plan', 'punish', 'survival', 'mirror'];

/**
 * C.5 P1 chessboards (Bug C2) — masquage des modes sans contenu.
 *
 * Les 33 ouvertures importées depuis Lichess Elite DB (PR #95 T8.b
 * Option B) n'ont AUCUN tag `#critical` / `#plan` / `#trap` dans leur
 * PGN — l'import scripté ne les annote pas. Conséquence pré-fix :
 * Léon cliquait sur Plan/Punish, voyait « Préviens Valentin que ce
 * répertoire est cassé » et repartait frustré.
 *
 * Fix UX : masquer les boutons des modes sans matériel pédagogique.
 * Recall / Survival / Mirror restent visibles (ils opèrent sur les
 * `positions` plates, indépendantes des tags). Plan/Punish n'apparaissent
 * que si `critical` (positions taggées critical+plan) / `traps`
 * (positions taggées trap) ne sont pas vides.
 *
 * Follow-up P2 (hors P1) : annoter les 33 ouvertures Elite DB
 * manuellement (skill chess-content-validator) ou auto-annoter via
 * Lichess Masters stats (positions où eval saute ≥ 50 cp entre plies
 * populaires = critiques).
 */
function availableModes(hasCritical: boolean, hasTraps: boolean): Mode[] {
  return ALL_MODES.filter((m) => {
    if (m === 'plan') return hasCritical;
    if (m === 'punish') return hasTraps;
    return true; // recall, survival, mirror : pas de prérequis de tags
  });
}

// Barème XP : +3 par onResult('success') dans n'importe quel mode (drill court
// avec mémorisation théorique, cf. docs/XP_BAREME.md).
const XP_PER_SUCCESS = 3;

export function RepertoireDrillPage(): JSX.Element {
  const { slug = '' } = useParams<{ slug: string }>();
  const [mode, setMode] = useState<Mode>('recall');
  const [score, setScore] = useState<{ ok: number; ko: number }>({ ok: 0, ko: 0 });
  const [sessionXp, setSessionXp] = useState(0);
  const [xpBurst, setXpBurst] = useState<{ value: number; key: number } | null>(null);
  const sessionStartRef = useRef<number>(Date.now());

  const q = useQuery({
    queryKey: ['opening-detail', slug],
    queryFn: () => openingLabApi.fetchItems(slug),
    enabled: slug !== '',
  });

  const positions = useMemo(() => (q.data ? parsePgnTree(q.data.pgn) : []), [q.data]);
  const critical = useMemo(
    () => positions.filter((p) => p.tags.critical && p.tags.plan),
    [positions],
  );
  const traps = useMemo(() => positions.filter((p) => p.tags.trap), [positions]);
  const allPlans = useMemo(
    () =>
      Array.from(
        new Set(positions.map((p) => p.tags.plan).filter((x): x is string => Boolean(x))),
      ),
    [positions],
  );

  if (q.isPending) return <FrameMessage>Chargement du répertoire…</FrameMessage>;
  if (q.isError || !q.data) return <FrameMessage>Répertoire introuvable.</FrameMessage>;

  const rep = q.data.repertoire;
  const repertoireDetail = { ...rep, pgn: q.data.pgn };
  // C.5 P1 : modes affichables selon ce que le PGN propose. Si le mode
  // courant a disparu (e.g. navigation depuis répertoire taggé vers Elite
  // DB sans tags), retombe sur 'recall' qui marche partout.
  const visibleModes = availableModes(critical.length > 0, traps.length > 0);
  const effectiveMode: Mode = visibleModes.includes(mode) ? mode : 'recall';
  const modeMeta = MODE_META[effectiveMode];

  function recordResult(
    r: 'success' | 'partial' | 'fail',
    meta?: { awardedXp?: boolean },
  ): void {
    setScore((s) => ({
      ok: s.ok + (r === 'success' ? 1 : 0),
      ko: s.ko + (r === 'fail' ? 1 : 0),
    }));
    // Fix idempotence permanente 2026-05-17 : si le backend a skippé
    // l'award (meta.awardedXp === false), on n'incrémente PAS le XP local
    // ni le burst — sinon Léon voit +3 XP local sur une position déjà
    // drillée, qui disparaît au refresh (divergence). Cas legacy (modes
    // Plan/Mirror/Survival/Punish qui ne persistent pas XP backend) :
    // `meta` est undefined → comportement legacy préservé (XP local
    // visuel qui disparaît au refresh).
    const skipXp = r === 'success' && meta?.awardedXp === false;
    if (r === 'success' && !skipXp) {
      setSessionXp((x) => x + XP_PER_SUCCESS);
      setXpBurst({ value: XP_PER_SUCCESS, key: Date.now() });
    }
  }

  const total = score.ok + score.ko;
  const successRate = total > 0 ? Math.round((score.ok / total) * 100) : 0;

  const chips = [
    { label: 'Mode', value: modeMeta.title, color: 'var(--violet-500)' },
    { label: 'Réussite', value: `${successRate}%`, color: 'var(--volt-500)' },
  ];

  return (
    <>
      <FixedNav chips={chips} />
      <main className="mx-auto max-w-[1280px] px-6 py-6 flex flex-col gap-5">
        {/* Header condensé */}
        <header className="flex flex-col gap-2">
          <div className="flex items-center gap-2 text-body-sm">
            <Link
              to="/opening-lab"
              className="text-ink-300 hover:text-paper-50 transition-all duration-150"
            >
              ← Opening Lab
            </Link>
            <span className="font-mono text-[11px] text-ink-300">· {rep.family}</span>
          </div>
          <div className="flex flex-wrap items-end justify-between gap-4">
            <div>
              <h1 className="font-display text-heading-1 text-paper-50">{rep.display_name}</h1>
              <div className="mt-1.5 flex items-center gap-3 flex-wrap">
                <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full border border-violet-500 text-violet-500 text-[11px] font-mono">
                  <span
                    aria-hidden="true"
                    className="size-1.5 rounded-full bg-violet-500"
                  />
                  {rep.color === 'white' ? 'Blancs' : 'Noirs'}
                  {rep.eco_code ? ` · ${rep.eco_code}` : ''}
                </span>
                <span className="font-mono text-xs text-ink-300">
                  <span className="text-paper-50">{positions.length}</span> positions ·{' '}
                  <span className="text-paper-50">{critical.length}</span> critiques avec plan ·{' '}
                  <span className="text-paper-50">{traps.length}</span> pièges
                </span>
              </div>
            </div>
            <div className="flex items-center gap-4 font-mono text-xs">
              <div className="flex flex-col items-end gap-0.5">
                <Caption>Session</Caption>
                <SessionTimer startedAtMs={sessionStartRef.current} />
              </div>
              <span className="text-ink-700">·</span>
              <div className="flex flex-col items-end gap-0.5">
                <Caption>Réussite</Caption>
                <div className="flex items-center gap-1.5 text-sm">
                  <AnimatedNumber
                    value={successRate}
                    format={(v) => `${Math.round(v)}%`}
                    style={{
                      color: successRate >= 75 ? 'var(--volt-500)' : 'var(--amber-500)',
                      fontWeight: 500,
                    }}
                  />
                  <span className="text-ink-700">·</span>
                  <span className="text-volt-500">✓{score.ok}</span>
                  <span className="text-coral-500">✗{score.ko}</span>
                </div>
              </div>
              <span className="text-ink-700">·</span>
              <div className="flex flex-col items-end gap-0.5">
                <Caption>XP session</Caption>
                <span className="font-mono text-sm text-acid-500 font-medium tabular-nums">
                  +{sessionXp} pts
                </span>
              </div>
            </div>
          </div>
        </header>

        {/* Mode pills — C.5 P1 : seuls les modes avec matériel pédagogique
            sont affichés. Plan masqué si 0 position critical+plan, Punish
            masqué si 0 position trap. Recall/Survival/Mirror toujours OK. */}
        <nav
          className="flex flex-wrap items-center gap-3 p-3 bg-ink-800 border border-ink-700 rounded-xl"
          data-testid="repertoire-mode-nav"
        >
          <div className="flex items-center gap-1.5">
            {visibleModes.map((m) => {
              const meta = MODE_META[m];
              const active = effectiveMode === m;
              return (
                <button
                  key={m}
                  type="button"
                  onClick={() => setMode(m)}
                  aria-label={meta.title}
                  title={meta.title}
                  data-testid={`repertoire-mode-${m}`}
                  className={`inline-flex items-center justify-center w-10 h-10 rounded-lg border font-display text-lg transition-all duration-150 ${
                    active
                      ? 'border-violet-500 bg-violet-500/15 text-paper-50'
                      : 'border-ink-700 bg-ink-900 text-ink-300 hover:border-ink-600 hover:text-paper-50'
                  }`}
                >
                  {meta.icon}
                </button>
              );
            })}
          </div>
          <div className="flex flex-col min-w-0">
            <span className="font-display text-base text-paper-50 font-semibold">
              {modeMeta.title}
            </span>
            <span className="font-mono text-xs text-ink-300">{modeMeta.desc}</span>
          </div>
        </nav>

        {/* Mode content — préserve intégralement les 5 composants MVP1.1 */}
        <section className="rounded-xl border border-ink-700 bg-ink-800/40 p-5 relative">
          {xpBurst && (
            <XpFloater
              value={xpBurst.value}
              tokenKey={xpBurst.key}
              caption="Coup d'ouverture trouvé"
              className="top-3 right-5"
            />
          )}
          {effectiveMode === 'recall' && positions.length > 0 && (
            <DrillRecall repertoire={repertoireDetail} positions={positions} onResult={recordResult} />
          )}
          {effectiveMode === 'plan' && (
            <DrillPlan
              repertoire={repertoireDetail}
              critical={critical}
              allPlans={allPlans}
              onResult={recordResult}
            />
          )}
          {effectiveMode === 'punish' && (
            <DrillPunish repertoire={repertoireDetail} traps={traps} onResult={recordResult} />
          )}
          {effectiveMode === 'survival' && positions.length > 0 && (
            <DrillSurvival repertoire={repertoireDetail} positions={positions} onResult={recordResult} />
          )}
          {effectiveMode === 'mirror' && positions.length > 0 && (
            <DrillMirror repertoire={repertoireDetail} positions={positions} onResult={recordResult} />
          )}
          {positions.length === 0 && (
            <p className="text-ink-300 italic">
              Aucune position extraite. Préviens Valentin que ce répertoire est cassé.
            </p>
          )}
        </section>

        {/* Notes du répertoire (préservé) */}
        {rep.notes && (
          <aside className="rounded-xl glass glass-violet p-5">
            <Caption color="violet">Notes</Caption>
            <pre className="mt-2 whitespace-pre-wrap font-mono text-body-sm text-ink-300 leading-relaxed">
              {rep.notes}
            </pre>
          </aside>
        )}
      </main>
    </>
  );
}

function FrameMessage({ children }: { children: ReactNode }): JSX.Element {
  return (
    <>
      <FixedNav chips={[]} />
      <main className="mx-auto max-w-[1280px] px-6 py-8 flex flex-col gap-4">
        <Link
          to="/opening-lab"
          className="text-caption uppercase text-ink-300 hover:text-paper-50 transition-all duration-150"
        >
          ← Tous les répertoires
        </Link>
        <p className="text-ink-300 italic">{children}</p>
      </main>
    </>
  );
}
